import OnlineAppComponent from "../lib/online_app_component";
import MapFilter from "../models/map_filter";
import MapBounds from "../models/map_bounds";
import Experience from "../models/experience";
import Subpage from "../models/subpage";
import {getElementComponentOfType} from "../../lib/global_components";
import {PaneOptionComponentConfig} from "../nav/components/pane_option_component";
import {PaneLinkComponentConfig} from "../nav/components/pane_link_component";
import {PaneInfoComponentConfig} from "../nav/components/pane_info_component";
import {NAV_OPTION_SELECTED_WITH_ROUTE_REQUEST_EVENT} from "../../constants/online_app_nav_option_events";

export const OnlineAppNavOptionConfig = {
  componentTypeKey: "OnlineAppNavOption",
  baseHtmlClass: "nav_option",
  selector: ".nav_option",
  mapFilterLinkSelector: ".map_filter__link",
  linkSelector: ".nav_option__link",
  infoSelector: ".nav_option__info",

  datasetConfigKey: "nav_option__online_app_data",
  identifierDatasetConfigKey: "identifier",
  statsIdentifierDatasetConfigKey: "stats_identifier",
  titleDatasetConfigKey: "title",
  mapBoundsDatasetConfigKey: "map_bounds",
  geoDataUrlDatasetConfigKey: "geo_data_url",

  subpageIdentifierDatasetConfigKey: "subpage.identifier",
  subpageSlugDatasetConfigKey: "subpage.slug",
  subpageNameDatasetConfigKey: "subpage.name",

  mapFilterIdentifierDatasetConfigKey: "map_filter.identifier",
  mapFilterSlugDatasetConfigKey: "map_filter.slug",
  mapFilterNameDatasetConfigKey: "map_filter.name",

  experienceIdentifierDatasetConfigKey: "experience.identifier",
  experienceSlugDatasetConfigKey: "experience.slug",
  experienceNameDatasetConfigKey: "experience.name",

  // The following is deprecated in favour of getting config via the dataset config JSON (above).
  identifierDatasetKey: "nav_option__identifier",

  // The event string lives in constants/online_app_nav_option_events.js so the Nav side
  // (pane_option_component.js) can listen without importing this module — importing it drags
  // the whole online-app state machine into the nav bundle (see the constant's doc comment).
  selectedWithRouteRequestEventName: NAV_OPTION_SELECTED_WITH_ROUTE_REQUEST_EVENT,
};

export class OnlineAppNavOption extends OnlineAppComponent {
  constructor(id_or_element, onlineApp, onlineAppNav) {
    super(id_or_element, onlineApp, OnlineAppNavOptionConfig);
    if (!this.element) {
      return;
    }
    this.onlineAppNav = onlineAppNav;

    // Rather than individual data attributes being used for config we should move to using the JSON in the `datasetConfigKey` data attribute.
    this.setDatasetConfig(OnlineAppNavOptionConfig.datasetConfigKey);
    this.identifier = this.getIdentifier(this.element);
    this.statsIdentifier = this.getStatsIdentifier(this.element);
    this.title = this.getTitle();
    this.mapBounds = this.getMapBounds() || null;
    this.geoDataUrl = this.getGeoDataUrl() || null;
    this.mapFilter = this.getMapFilter(); // When the nav option is a map filter nav option.
    this.subpage = this.getSubpage(); // When the nav option is a subpage nav option.
    this.experience = this.getExperience(); // When the nav option is a experience nav option.

    this.linkElement = this.element.querySelector(
      OnlineAppNavOptionConfig.linkSelector,
    );
    if (this.linkElement) {
      this.linkElement.addEventListener(PaneLinkComponentConfig.clickedEventName, this.handleClickedViaPaneLink.bind(this));
    }

    this.infoElement = this.element.querySelector(
      OnlineAppNavOptionConfig.infoSelector,
    );
    if (this.infoElement) {
      this.infoElement.addEventListener(PaneInfoComponentConfig.clickedEventName, this.handleClickedViaPaneInfo.bind(this));
    }

  }

  handleClickedViaPaneLink(event) {
    this.dispatchAction("navOptionClicked", this);
  }

  handleClickedViaPaneInfo(event) {
    this.dispatchAction("navOptionClicked", this);
  }

  getIdentifier() {
    const identifier = this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.identifierDatasetConfigKey,
    );
    if (!identifier) return;
    return identifier;
  }

  getStatsIdentifier() {
    const identifier = this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.statsIdentifierDatasetConfigKey,
    );
    if (!identifier) return;
    return identifier;
  }

  getTitle() {
    const title = this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.titleDatasetConfigKey,
    );
    if (!title) return;
    return title;
  }

  // Returns the title that is used on the map.
  // Only map filter nav options have a map title.
  getMapTitle() {
    if (!this.mapFilter) return;
    return this.title;
  }

  getMapBounds() {
    const bounds = this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.mapBoundsDatasetConfigKey,
    );
    if (!bounds) return;
    return new MapBounds({
      sw: bounds.sw,
      ne: bounds.ne,
    });
  }

  getGeoDataUrl() {
    const url = this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.geoDataUrlDatasetConfigKey,
    );
    if (!url) return;
    return url;
  }

  hasGeoDataUrl() {
    return !!this.geoDataUrl;
  }

  // Either has geo data already or the potential to have geo data via a URL.
  hasGeoData() {
    return (
      (!!this.geoData && typeof this.geoData === "object") ||
      this.hasGeoDataUrl()
    );
  }

  // Returns a promise that resolves to null or a geoJSON JSON object
  async getGeoData() {
    if (!!this.geoData) return this.geoData;
    return await this.getGeoDataFromUrl().then((geoData) => {
      this.geoData = geoData;
      return this.geoData;
    });
  }

  // Return a promise that resolves to javascript object.
  async getGeoDataFromUrl() {
    if (!this.hasGeoDataUrl()) return;
    const response = await fetch(this.geoDataUrl);
    return response.json();
  }

  // TODO - experience IDs should really be extracted from a dataset config value on the nav option element! The native apps should do the same.
  // We currently get the IDs from the child map filter link element.
  getExperienceIds() {
    let experienceIds = [];
    const mapFilterLinkElement = this.element.querySelector(
      OnlineAppNavOptionConfig.mapFilterLinkSelector,
    );
    if (mapFilterLinkElement) {
      let ids = mapFilterLinkElement.dataset["experience_ids"];
      if (ids) {
        ids = ids.split(",");
        ids = ids.map(function (id) {
          return parseInt(id.trim());
        });
        experienceIds = experienceIds.concat(ids);
      }
    }
    let uniqueExperienceIds = [];
    experienceIds.forEach((id, index) => {
      if (experienceIds.indexOf(id) === index) {
        uniqueExperienceIds.push(id);
      }
    });
    // experienceIds.filter(onlyUnique); TODO - enable this once prototype.js is no longer a dependency. This usage is incompatible with prototype.js Array#filter method.
    return uniqueExperienceIds;
  }

  getSubpageName() {
    return this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.subpageNameDatasetConfigKey,
    );
  }

  getSubpageSlug() {
    return this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.subpageSlugDatasetConfigKey,
    );
  }

  getSubpageIdentifier() {
    return this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.subpageIdentifierDatasetConfigKey,
    );
  }

  getSubpage() {
    if (!this.getDatasetConfigValue("subpage")) return;
    return new Subpage(
      {
        name: this.getSubpageName(),
        code: this.getSubpageIdentifier(),
        slug: this.getSubpageSlug(),
      },
      this,
    );
  }

  getMapFilterName() {
    return this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.mapFilterNameDatasetConfigKey,
    );
  }

  getMapFilterSlug() {
    return this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.mapFilterSlugDatasetConfigKey,
    );
  }

  getMapFilterIdentifier() {
    return this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.mapFilterIdentifierDatasetConfigKey,
    );
  }

  getMapFilter() {
    if (!this.getDatasetConfigValue("map_filter")) return;
    return new MapFilter(
      {
        name: this.getMapFilterName(),
        code: this.getMapFilterIdentifier(),
        slug: this.getMapFilterSlug(),
        experienceIds: this.getExperienceIds(),
      },
      this,
    );
  }

  getExperienceName() {
    return this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.experienceNameDatasetConfigKey,
    );
  }

  getExperienceSlug() {
    return this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.experienceSlugDatasetConfigKey,
    );
  }

  getExperienceIdentifier() {
    return this.getDatasetConfigValue(
      OnlineAppNavOptionConfig.experienceIdentifierDatasetConfigKey,
    );
  }

  // Maybe one day we'll find the experience within the global collection of experiences (rather than create a fresh one).
  // But for the purposes of this nav option we currently only need the experience to create a URL for navigating to via the router.
  getExperience() {
    if (!this.getDatasetConfigValue("experience")) return;
    const experienceId = this.getExperienceIdentifier();
    return new Experience(
      {
        name: this.getExperienceName(),
        code: this.getExperienceIdentifier(),
        slug: this.getExperienceSlug(),
      },
      null,
      this.onlineApp,
    );
  }

  /**
   * Determines if the nav option will show a pane of content when selected.
   * We need to check the nav's PaneOptionComponent as this has the logic to determine if a pane will be shown.
   *
   * @return {boolean}
   */
  willShowNavPane() {
    const paneOptionComponent = getElementComponentOfType(this.element, PaneOptionComponentConfig.componentTypeKey);
    if (!paneOptionComponent && !paneOptionComponent.shouldOpenPane) return;
    return paneOptionComponent.shouldOpenPane();
  }

  /**
   * Determines if the nav should be closed when the nav option is selected (clicked or via url request).
   * This only comes into play when the online app is in `modal` mode (see nav_option_helpers.js selectNavOption function).
   * When not in `modal` the nav always stays open when a nav option is selected.
   * In `modal` mode, the nav should be closed when the Nav is not showing a Pane of content.
   * @return {boolean} If the nav should be closed.
   */
  shouldCloseNav() {
    return !this.willShowNavPane();
  }

  renderSelectedState(state) {
    if (state) {
      this.addHtmlClassState("active");
    } else {
      this.removeHtmlClassState("active");
    }
  }

  render() {
    if (this.readState("navOption") === this) {
      this.renderSelectedState(true);
    } else {
      this.renderSelectedState(false);
    }
  }

  /**
   * Fire an event on the element that indicates the nav option has been selected with a route request (url change).
   * This is only fired when the OnlineApp interrogates the URL and we want to select a nav option based on the URL params.
   * For example, the url might be /filters/foo and we find the `foo` map filter and select its 'foo' nav option.
   * The Nav's PaneOptionComponent listens and will select the appropriate option in the nav.
   * See `app/javascript/rankers/online_apps/nav/components/pane_option_component.js`
   */
  // `isInitialRoute` marks the FIRST route resolution after boot (the deep-link restore). The Nav
  // uses it to suppress the pane option's nav-toggle instruction for that one resolution, so a
  // reload of a deep link (e.g. /filters/campgrounds) keeps the just-restored persisted navOpen
  // instead of the option's close instruction clobbering it (plan 2026-07-20 follow-up). This is a
  // web-host-only signal — native has no OnlineAppRouter and never fires this event.
  fireSelectedWithRouteRequestEvent(options = {}) {
    const event = new CustomEvent(
      OnlineAppNavOptionConfig.selectedWithRouteRequestEventName,
      { detail: { isInitialRoute: !!options.isInitialRoute } },
    );
    this.element.dispatchEvent(event);
  }
}
